import { ReactNode, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';

import {
  GetSongpopMaestroFeatureQuery,
  UpdateSongpopMaestroConfigMutation,
  UpdateSongpopMaestroConfigMutationVariables,
} from '../../../../../../__gqltypes__';
import { SONGPOP_MAESTRO_FEATURE_QUERY, UPDATE_SONGPOP_MAESTRO_CONFIG } from '../graphql';

/**
 * Global feature switch (GameConfig features.songpopMaestro `enabled`), checked before any drop
 * schedule. Saved on its own, so toggling it never resends the perk values of the Settings tab.
 */
export function MaestroPackFeatureSwitch({ children }: { children?: ReactNode }) {
  const { data, loading, refetch } = useQuery<GetSongpopMaestroFeatureQuery>(SONGPOP_MAESTRO_FEATURE_QUERY, {
    fetchPolicy: 'no-cache',
  });
  const [updateConfig, { loading: saving, error }] = useMutation<
    UpdateSongpopMaestroConfigMutation,
    UpdateSongpopMaestroConfigMutationVariables
  >(UPDATE_SONGPOP_MAESTRO_CONFIG, { onCompleted: () => refetch() });
  const [pending, setPending] = useState<boolean | null>(null);

  const enabled = data?.liveOps.songpopMaestroConfig.enabled;

  const apply = () => {
    if (pending === null) {
      return;
    }
    updateConfig({ variables: { input: { enabled: pending } } })
      .catch(() => {
        // surfaced through error
      })
      .finally(() => setPending(null));
  };

  return (
    <div className="mb-3">
      <div className="d-flex align-items-center">
        <h4 className="mb-0 mr-3">Songpop Maestro</h4>
        {loading || enabled === undefined ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <>
            <Form.Check
              type="switch"
              id="maestro-feature-enabled"
              label=""
              checked={enabled}
              disabled={saving}
              onChange={(e) => setPending(e.target.checked)}
            />
            <Badge variant={enabled ? 'success' : 'secondary'}>{enabled ? 'Sales ON' : 'Sales OFF'}</Badge>
            {saving && <Spinner animation="border" size="sm" className="ml-2" />}
          </>
        )}
        {/* the other page-wide switch (perks), next to this one */}
        {children && <div className="ml-4 d-flex align-items-center">{children}</div>}
      </div>
      <small className="text-muted">
        Sales: checked before the event schedule. Off hides the offer and stops the pushes; a payment in progress is
        still granted and owners keep their perks. Perks: emergency kill switch for every owner.
      </small>
      {error && (
        <Alert variant="danger" className="mt-2">
          {error.message}
        </Alert>
      )}

      <Modal show={pending !== null} onHide={() => setPending(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{pending ? 'Turn the feature on?' : 'Turn the feature off?'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pending
            ? 'Every running drop becomes visible to its audience, on beta and prod, within 5 minutes.'
            : 'The offer disappears and the pushes stop, on beta and prod, within 5 minutes. Owners keep their perks.'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button variant={pending ? 'success' : 'danger'} onClick={apply}>
            {pending ? 'Turn on' : 'Turn off'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
